import { CSSProperties, useEffect, useState } from 'react';
import Select, { components as ReactSelectComponents, ControlProps, OptionProps, SingleValueProps, StylesConfig } from 'react-select';
import { Color, Size, Styles } from '@vivli/shared/theme';
import { AdvancedHeaderComponent } from './advanced-header.component';
import { ISelectOption } from '@vivli/features/search/infrastructure/interface';
import { DTIDropdownMenu } from '@vivli/shared/infrastructure/constants';

const headerStyle: CSSProperties = {
    color: Color.DARK_GRAY,
    fontSize: 16,
    marginBottom: 8
};

/* Selenium selects an option by data-test-id, and the id it expects is built by
 * dropdown-menu-item.component.tsx:37 on the legacy dropdown. This reproduces that
 * expression EXACTLY, quirk included: String.replace with a string argument swaps only
 * the FIRST space, so 'Early Phase 1' yields 'dropdown_menu_item_early_phase 1' with a
 * real space still in it. SeleniumWrapperMethods.selectDropDownOption() builds the same
 * string, so "fixing" this breaks every converted field's automation. (DEV-961) */
const getOptionDataId = (label?: string) => DTIDropdownMenu.DropdownMenuItem(label?.replace(' ', '_').toLowerCase());

const OptionWithDataIdComponent = (props: OptionProps<ISelectOption, false>) => (
    <ReactSelectComponents.Option
        {...props}
        innerProps={{ ...props.innerProps, 'data-test-id': getOptionDataId(props.data?.label) } as any}
    >
        {props.children}
    </ReactSelectComponents.Option>
);

/* The legacy dropdown stamped data-test-id on its clickable area (dropdown-menu.component.tsx:101),
 * so the id has to land on the control rather than an outer wrapper - Selenium clicks it to open
 * the menu, and a wrapper div is not clickable.
 *
 * The id arrives through selectProps rather than a closure so this component can stay at module
 * scope. A custom react-select component defined inside render is a new component type on every
 * render, which remounts the control and drops focus mid-keystroke. (DEV-961) */
const ControlWithDataIdComponent = (props: ControlProps<ISelectOption, false>) => (
    <ReactSelectComponents.Control
        {...props}
        innerProps={{ ...props.innerProps, 'data-test-id': (props.selectProps as any)?.dataId } as any}
    >
        {props.children}
    </ReactSelectComponents.Control>
);

/* The legacy dropdown rendered its selection through EllipsisTextView, which carries a
 * title attribute. Selenium reads the current selection with getAttribute("title") in six
 * page objects, so the replacement has to expose it too. (DEV-961) */
const SingleValueWithTitleComponent = (props: SingleValueProps<ISelectOption, false>) => (
    <ReactSelectComponents.SingleValue {...props} innerProps={{ ...props.innerProps, title: props.data?.label } as any}>
        {props.children}
    </ReactSelectComponents.SingleValue>
);

/* Applied only when controlHeight is supplied; omit the prop and none of this runs, so the
 * existing Search and metrics call sites keep their current appearance byte for byte.
 *
 * A single height on the control would not work. react-select builds the control's height
 * from five places, and three of them have their own floor: the control's minHeight (38 by
 * default), the control padding this file adds below, the valueContainer padding, the
 * hidden Input's margin+padding, and the dropdownIndicator padding. Miss any of the last
 * three and the control simply refuses to go below ~38px. (DEV-961) */
const compactStyles = (h: number): StylesConfig<ISelectOption, false> => ({
    control: (provided) => ({
        ...provided,
        minHeight: h,
        height: h,
        paddingTop: 0,
        paddingBottom: 0
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: h,
        padding: '0 8px'
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: h
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: h < 34 ? 4 : 8
    })
});

interface AdvancedSelectProps {
    isMulti?: boolean;
    isSearchable?: boolean;
    isClearable?: true;
    className?: string;
    title?: string;
    onChange: (selectedOptions: ISelectOption[]) => void;
    options: ISelectOption[];
    selectedOptions?: ISelectOption[];
    targetStyle?: CSSProperties;
    fieldStyle?: CSSProperties;
    isDisabled?: boolean;
    sort?: 'asc' | 'desc';
    menuPosition?: 'fixed' | 'absolute';
    menuPlacement?: 'auto' | 'bottom' | 'top';
    onBlur?: (e: any) => void;
    //omit to keep react-select's own sizing, which is the current appearance
    controlHeight?: number;
    //stamped on the control so Selenium can find the field, matching dropdown-menu.component.tsx:101
    dataId?: string;
    name?: string;
    //overrides the default prompt; carries DropdownFieldComponent's selectText across
    placeholder?: string;
}

export const AdvancedSelectComponent = ({
    isMulti,
    isSearchable,
    isClearable,
    className,
    title,
    onChange,
    options,
    selectedOptions,
    targetStyle,
    fieldStyle,
    isDisabled,
    sort,
    menuPosition = 'fixed',
    menuPlacement = 'auto',
    onBlur,
    controlHeight,
    dataId,
    name,
    placeholder
}: AdvancedSelectProps) => {
    const [activeOptions, setActiveOptions] = useState<ISelectOption[]>();

    const baseStyles: StylesConfig<ISelectOption, false> = {
        control: (provided) => ({
            ...provided,
            ...selectStyle,
            paddingLeft: Size.PADDING
        }),
        menu: (selectStyle) => ({
            ...selectStyle
        }),
        menuList: (menuStyle) => ({ ...menuStyle, width: '99%' }),
        menuPortal: (style) => ({ ...style, zIndex: '9999' }),
        option: (styles) => ({
            ...styles,
            backgroundColor: Color.ADVANCED_MENU_TARGET_BACKGROUND_COLOR,
            color: Color.DARK_GRAY,
            ':hover': {
                backgroundColor: Color.HOVER_GRAY,
                color: Color.DARK_GRAY
            },
            ':active': {
                backgroundColor: Color.HOVER_GRAY,
                color: Color.DARK_GRAY
            }
        }),
        multiValue: (mvStyle) => ({
            ...mvStyle,
            backgroundColor: 'rgb(176, 176, 176)',
            color: 'white',
            borderRadius: 6
        }),
        multiValueLabel: (mvLabelStyle) => ({
            ...mvLabelStyle,
            color: 'white'
        })
    };

    const selectStyle: CSSProperties = {
        position: 'relative',
        backgroundColor: Color.ADVANCED_MENU_TARGET_BACKGROUND_COLOR,
        paddingTop: 8,
        paddingBottom: 8,
        boxShadow: Size.BOX_SHADOW_EXTRA_LIGHT,
        display: 'flex',
        border: 'none',
        fontSize: Size.FontSize.Large,
        color: Color.DARK_GRAY,
        cursor: 'pointer',
        ...Styles.borderRadius(Size.BORDER_RADIUS),
        ...Styles.NO_SELECT,
        ...targetStyle,
        pointerEvents: isDisabled ? 'none' : 'auto'
    };

    /* control is the one key both halves define, so it has to compose rather than replace:
     * the base carries the caller's targetStyle, the height override then removes the
     * padding and the 38px floor on top of it. Every other compact key is new. */
    const heightStyles = controlHeight ? compactStyles(controlHeight) : undefined;
    const selectStyles: StylesConfig<ISelectOption, false> = !heightStyles
        ? baseStyles
        : {
              ...baseStyles,
              ...heightStyles,
              control: (provided, state) => heightStyles.control(baseStyles.control(provided, state), state)
          };

    const placeholderText = placeholder || (isMulti ? 'Select Multiple' : 'Select One');

    const handleOnChange = (value: any) => {
        if (Array.isArray(value)) {
            onChange(value);
        } else {
            onChange([value]);
        }
    };

    useEffect(() => {
        if (!sort) {
            setActiveOptions(options);
            return;
        }

        if (sort === 'asc') {
            const sortedOptions = options?.sort((a, b) => a.label.localeCompare(b.label));
            setActiveOptions(sortedOptions);
            return;
        }

        const sortedOptions = options?.sort((a, b) => b.label.localeCompare(a.label));
        setActiveOptions(sortedOptions);
    }, [options]);

    return (
        <div>
            {title && <AdvancedHeaderComponent title={title} style={{ ...headerStyle, ...fieldStyle }} />}
            <Select
                classNamePrefix={'vivli-select'}
                {...({ dataId: dataId || name } as any)}
                components={
                    {
                        Control: ControlWithDataIdComponent,
                        Option: OptionWithDataIdComponent,
                        SingleValue: SingleValueWithTitleComponent
                    } as any
                }
                value={selectedOptions}
                options={activeOptions}
                isSearchable={isSearchable}
                isMulti={isMulti as any}
                isClearable={isClearable}
                placeholder={placeholderText}
                onChange={handleOnChange}
                backspaceRemovesValue
                className={className}
                isDisabled={isDisabled}
                closeMenuOnSelect={!isMulti}
                /* Render the menu into document.body. Without this the menu is a child of the
                 * control, so any ancestor with overflow hidden/auto clips it - which is every
                 * grid row and scrolling panel in the app, where only the first option or two
                 * were visible. menuPosition 'fixed' is what keeps a portalled menu tracking
                 * its control while the page or grid scrolls. The menuPortal z-index above has
                 * been here all along waiting for a target. (DEV-961) */
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition={menuPosition}
                menuPlacement={menuPlacement}
                onBlur={onBlur}
                styles={selectStyles}
            />
        </div>
    );
};
